import { useState } from "react";
import { motion, useMotionValueEvent } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLeadDialog } from "@/components/site/LeadDialog";
import { useDrivenStyle, mapRange } from "./timeline";

/**
 * HeroOverlay - Shot 1 content. Lives INSIDE the pinned scene: visible at
 * progress 0 over the bright background with the closed laptop to the right,
 * then slides/fades away as scroll begins.
 */

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.12 },
  }),
};

export default function HeroOverlay({ progress }) {
  const { openPreview } = useLeadDialog();
  const [gone, setGone] = useState(false);
  useMotionValueEvent(progress, "change", (p) => setGone(p > 0.09));

  const rootRef = useDrivenStyle(progress, (el, p) => {
    el.style.opacity = String(mapRange(p, 0.015, 0.085, 1, 0));
    el.style.transform = `translateX(${mapRange(p, 0.015, 0.1, 0, -90)}px)`;
  });
  const hintRef = useDrivenStyle(progress, (el, p) => {
    el.style.opacity = String(mapRange(p, 0.005, 0.04, 1, 0));
  });

  return (
    <div
      ref={rootRef}
      data-testid="hero-overlay"
      style={{ visibility: gone ? "hidden" : "visible" }}
      className="absolute inset-0 z-10 pointer-events-none"
    >
      <div className="h-full max-w-[92rem] mx-auto px-5 md:px-10 flex flex-col justify-center">
        <div className="max-w-[46rem] -mt-[4vh]">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="flex items-center gap-5 text-[11px] uppercase tracking-[0.24em] font-medium text-black/55 font-body"
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Studio · Ireland
            </span>
            <span className="hidden sm:inline-flex items-center gap-2">
              <span className="w-6 h-px bg-black/20" />
              Now booking
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-7 font-display font-bold uppercase text-[clamp(2.1rem,4.7vw,4.9rem)] leading-[0.96] tracking-tight text-[color:var(--ww-charcoal)]"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
                High-performing
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-brand-gradient">
                websites &amp; software
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block">
                for service businesses.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-7 text-base md:text-lg text-black/60 leading-relaxed font-body max-w-md"
          >
            Modern websites and digital systems that help service businesses
            look professional, run efficiently and grow, designed and built in
            Ireland.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.72 }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <button
              type="button"
              onClick={() => openPreview()}
              data-testid="hero-cta-primary"
              className="pointer-events-auto group w-fit inline-flex items-center gap-3 pl-5 pr-2 py-2 rounded-full bg-[color:var(--ww-charcoal)] text-white text-sm font-semibold hover:bg-black transition-colors font-body"
            >
              Get Your Free Website Preview
              <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 text-black">
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </button>
            <a
              href="#work"
              data-testid="hero-cta-secondary"
              className="pointer-events-auto w-fit inline-flex items-center gap-2 text-sm font-medium text-black/65 hover:text-black transition-colors font-body"
            >
              View our work
              <ArrowDown className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </div>

      <div
        ref={hintRef}
        className="absolute bottom-7 inset-x-0 flex justify-center"
      >
        <span className="flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-black/45 font-medium font-body">
          <span className="w-8 h-px bg-black/25" />
          Scroll: the laptop opens
          <span className="w-8 h-px bg-black/25" />
        </span>
      </div>
    </div>
  );
}
